import { CloudServices, Editor } from 'ckeditor5';
import {
  CloudServicesCommentsAdapter,
  CommentsRepository,
  NarrowSidebar,
  PresenceList,
  WideSidebar,
} from 'ckeditor5-premium-features';
import { DocumentUser, QualioDocument } from '../api/model/document';
import { Tag } from '../api/model/tag';
import { MedtechUserV2 } from '../api/user';
import { exportPdfConfig } from '../plugins/PdfExport/QualioPdfExport';
import { qualioSuggestionThreadBuilder } from '../views/ckeditor/QualioSuggestionThreadView';
import { CKEditorConfig } from './CKEditorConfig';
import { ContextConfigFactory } from './ContextConfigFactory';
import {
  ContextConfigProperties,
  IndependentContextConfigProperties,
} from './DocumentEditorConfig.types';
import { DocumentEditorConfigFactory } from './DocumentEditorConfigFactory';
import {
  getContentEditorPlugins,
  getEditorAiPlugins,
  getMentionPluginConfig,
} from './Plugins';
import { AI_TOOLBAR_ITEMS, CONTENT_EDITOR_TOOLBAR_ITEMS } from './ToolbarItems';

export type SectionConfigProperties = {
  collaborationEntityId: string;
  collaborationEntityName: string;
  sectionId: number;
  documentId: number;
  documentStatus: string;
  documentOwner: DocumentUser;
  companyId: number;
  handleAutoSave: (editor: Editor) => Promise<void>;
  commentsPermission: number;
  entityType: string;
  editorAiEnabled?: boolean;
  qualioDocument: QualioDocument;
  users?: MedtechUserV2[];
  tags?: Tag[];
};

export type SidebarConfig = {
  sidebarElement: Element | undefined | null;
  preventScrollOutOfView: boolean;
};

export type PresenceListConfig = {
  containerElement: Element | undefined | null;
};

export const getEditorConfig = (
  sectionConfigProperties: SectionConfigProperties,
): CKEditorConfig => {
  let configFactory = new DocumentEditorConfigFactory();
  const contentEditorPlugins = getContentEditorPlugins();

  const reusableContentAllowed =
    sectionConfigProperties.entityType !== 'change.section';

  const plugins = [
    ...(sectionConfigProperties.editorAiEnabled ? getEditorAiPlugins() : []),
    ...(reusableContentAllowed ? [CKReusableContent.ReusableContent] : []),
    ...contentEditorPlugins,
  ];

  configFactory = configFactory
    .withCompanyId(sectionConfigProperties.companyId)
    .withPlugins(plugins)
    .withAutosave((editor: Editor): Promise<void> => {
      // Must return a promise which resolves upon successful save
      return sectionConfigProperties.handleAutoSave(editor);
    })
    .withComments(
      sectionConfigProperties.commentsPermission,
      sectionConfigProperties.qualioDocument,
      sectionConfigProperties.users,
      sectionConfigProperties.tags,
    )
    .withCollaboration(
      `company-${sectionConfigProperties.companyId}-${sectionConfigProperties.collaborationEntityName}-${sectionConfigProperties.collaborationEntityId}-section-${sectionConfigProperties.sectionId}`,
    )
    .withTrackChanges(
      qualioSuggestionThreadBuilder(
        sectionConfigProperties.qualioDocument,
      ) as any,
    )
    .withPDFExport(exportPdfConfig)
    .withMention(getMentionPluginConfig(sectionConfigProperties.companyId))
    .withSectionId(sectionConfigProperties.sectionId)
    .withDocumentId(sectionConfigProperties.documentId)
    .withEntityType(sectionConfigProperties.entityType)
    .withFontSize(false)
    .withFontFamily(false)
    .withLists();

  if (sectionConfigProperties.editorAiEnabled) {
    configFactory = configFactory.withAI();
  }

  if (reusableContentAllowed) {
    configFactory = configFactory.withReusableContent(
      sectionConfigProperties.companyId,
      sectionConfigProperties.documentStatus,
    );
  } else {
    configFactory.withMediaEmbed();
  }

  return configFactory.getConfig();
};

export const getContextConfig = (
  contextConfigProperties: ContextConfigProperties,
): any => {
  let contextConfigFactory = new ContextConfigFactory();

  contextConfigFactory = contextConfigFactory
    .withCloudService(
      contextConfigProperties.collaborationToken,
      contextConfigProperties.editorBundleVersion,
    )
    .withPlugins([CloudServices, NarrowSidebar, WideSidebar, PresenceList])
    .withToolbarItems(CONTENT_EDITOR_TOOLBAR_ITEMS)
    .withSidebar(contextConfigProperties.sidebarConfig)
    .withPresenceList(contextConfigProperties.presenceListConfig);

  if (contextConfigProperties.editorAiEnabled) {
    contextConfigFactory = contextConfigFactory.withToolbarItems([
      ...AI_TOOLBAR_ITEMS,
      ...CONTENT_EDITOR_TOOLBAR_ITEMS,
    ]);
  }

  return contextConfigFactory.getConfig();
};

export const getIndependentContextConfig = ({
  collaborationToken,
  sidebarConfig,
  fileDocument,
  commentsPermission,
  users,
  tags,
}: IndependentContextConfigProperties): any => {
  const contextConfigFactory = new ContextConfigFactory();
  return contextConfigFactory
    .withPlugins([
      CloudServices,
      CloudServicesCommentsAdapter,
      CommentsRepository,
      NarrowSidebar,
      WideSidebar,
    ])
    .withCloudService(collaborationToken)
    .withSidebar(sidebarConfig)
    .withContextCollaboration(
      `company-${fileDocument.company_id}-document-${fileDocument.id}-section-${fileDocument.sections[0].id}`,
    )
    .withComments(commentsPermission, fileDocument, users, tags)
    .withCompanyId(fileDocument.company_id)
    .withDocumentId(fileDocument.id)
    .withLicenseKey()
    .getConfig();
};
